import { CardContent, CardHeader, Divider, Grid, Typography } from '@mui/material';
import { Group, ResolvedEvent } from '@thro-types/events';

import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { EntryWithHeadline } from 'app/components';
import { translations } from 'locales/i18n';

import { CustomCard } from '../../EventCard';
import { OrganizerGroupCardActions, ParticipantGroupCardActions } from '../../components/actions';
import { isPreCalc, removeDuplicates } from '../../helper';

interface GroupCardProps {
  group: Group;
  event: ResolvedEvent;
  canEdit: boolean;
  markedGroup: number | null;
  setMarkedGroup: Dispatch<SetStateAction<number | null>>;
  handleEnrollment: (group: any, enroll?: boolean) => void;
  handleCancelGroup: (selectedGroup: any) => void;
}

export const GroupCard = ({
  group,
  event,
  markedGroup,
  canEdit,
  setMarkedGroup,
  handleEnrollment,
  handleCancelGroup
}: GroupCardProps) => {
  const { t } = useTranslation();

  const CardAction = () => {
    return !group?.isCancelled && !event.isCancelled && event.waitingType !== 'EVENT' ? (
      canEdit ? (
        <OrganizerGroupCardActions
          event={event}
          group={group}
          handleEnroll={e => {
            handleEnrollment(group, true);
          }}
          handleUnenroll={e => {
            handleEnrollment(group, false);
          }}
          handleCancelGroup={handleCancelGroup}
        />
      ) : (
        <ParticipantGroupCardActions
          event={event}
          group={group}
          handleEnroll={e => {
            handleEnrollment(group, true);
          }}
          handleUnenroll={e => {
            handleEnrollment(group, false);
          }}
        />
      )
    ) : (
      <></>
    );
  };

  return (
    <CustomCard
      className={`highlightSelected--${markedGroup === group.id} ${
        group?.isCancelled ? 'groupCancelled' : ''
      }`}
      onClick={() => setMarkedGroup(group.id)}
      content={t(translations.pages.events.cancelled()).toUpperCase()}
    >
      <CardHeader
        title={
          <>
            {`${t(translations.pages.events.create.groupCard.group())} ${
              (event.groups?.findIndex(g => g.id === group.id) || 0) + 1
            }`}
            {canEdit && (
              <Typography fontSize={12} color={'lightslategray'}>{`(ID: ${group.id})`}</Typography>
            )}
          </>
        }
        action={<CardAction />}
      />
      <Divider />
      <CardContent>
        <Grid container className="groupCard" direction={'column'}>
          {group.info && (
            <Grid item container>
              <Grid item xs className="groupCard__headline-entry">
                <EntryWithHeadline
                  headline={t(translations.pages.events.create.groupCard.info())}
                  entry={`${group.info}`}
                />
              </Grid>
            </Grid>
          )}
          {group.place && (
            <Grid item container>
              <Grid item xs className="groupCard__headline-entry">
                <EntryWithHeadline
                  headline={t(translations.pages.events.create.groupCard.place())}
                  entry={`${group.place}`}
                />
              </Grid>
            </Grid>
          )}
          <Grid item xs className="groupCard__headline-entry">
            <Typography component={'h2'}>
              {t(translations.pages.events.create.groupCard.participants())}
            </Typography>
            <Grid item container>
              <Grid item xs={6}>
                <EntryWithHeadline
                  headline={t(translations.pages.events.create.groupCard.min())}
                  entry={`${group.minParticipants}`}
                />
              </Grid>
              <Grid item xs={6}>
                <EntryWithHeadline
                  headline={t(translations.pages.events.create.groupCard.max())}
                  entry={`${group.maxParticipants}`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item container>
            {!group.enrollments !== undefined && group.enrollments !== null && (
              <Grid item xs className="groupCard__headline-entry">
                <EntryWithHeadline
                  headline={
                    isPreCalc(event)
                      ? t(translations.pages.events.create.groupCard.currentlyPreCalc())
                      : t(translations.pages.events.create.groupCard.currently())
                  }
                  entry={`${
                    isPreCalc(event)
                      ? removeDuplicates(
                          [...group.preCalcEnrollments!, ...group.enrollments!],
                          item => item.id
                        ).length || 0
                      : group.enrollments?.length || 0
                  }`}
                />
              </Grid>
            )}
            {!group.waitingList !== undefined &&
              group.waitingList !== null &&
              event.waitingType !== 'EVENT' && (
                <Grid item xs className="groupCard__headline-entry">
                  <EntryWithHeadline
                    headline={
                      isPreCalc(event)
                        ? t(translations.pages.events.create.groupCard.peopleInWaitinglistPreCalc())
                        : t(translations.pages.events.create.groupCard.peopleInWaitinglist())
                    }
                    entry={`${
                      isPreCalc(event)
                        ? group.waitingList?.enrollments?.filter(e => !e.preCalcGroupId).length
                        : group.waitingList?.enrollments?.length || 0
                    }`}
                  />
                </Grid>
              )}
          </Grid>
        </Grid>
      </CardContent>
    </CustomCard>
  );
};
